/**
 * SPIN TENET — CFD Run Management Redux State Slice (Gate 2 Foundation)
 *
 * Manages:
 * - Current run creation drafting (explicit engine selection, preflight readiness)
 * - Immutable Run Specification snapshots
 * - Run lifecycle state progression ('QUEUED' -> 'RUNNING' -> 'COMPLETED' / 'FAILED')
 * - LocalStorage persistence of run history across reloads
 *
 * SCIENTIFIC INTEGRITY INVARIANT:
 * - Once created, RunRecord.specification is deeply immutable.
 * - Modifying scenario wind, geometry, or domain later DOES NOT alter existing runs.
 * - Zero large numerical arrays or raw buffers in Redux (boundary audit contract).
 */

import { createSlice, PayloadAction } from '@reduxjs/toolkit';
import type {
  EngineId,
  RunSpecification,
  RunLifecycleStatus,
  CfdResultSummary
} from '@contracts/index';

const RUN_STORAGE_KEY = 'spin_tenet_run_history_v1';

export interface RunRecord {
  runId: string;
  remoteRunId?: string;
  studyId: string;
  scenarioId: string;
  scenarioName: string;
  engine: EngineId;
  specificationVersion: string;
  specification: Readonly<RunSpecification>;
  status: RunLifecycleStatus;
  createdAt: string;
  completedAt?: string;
  error?: string;
  resultStatus: 'NONE' | 'PENDING' | 'AVAILABLE';
  resultSummary?: CfdResultSummary;
}

export interface RunState {
  runs: Record<string, RunRecord>;
  runOrder: string[];
  activeRunId: string | null;
  /** Explicit per-run engine selection in Run Setup. Strictly null by default. */
  selectedEngineId: EngineId | null;
  nextRunSequence: number;
}

function loadPersistedRunState(): RunState {
  const defaultState: RunState = {
    runs: {},
    runOrder: [],
    activeRunId: null,
    selectedEngineId: 'openfoam', // Default to OpenFOAM
    nextRunSequence: 1
  };

  if (typeof window === 'undefined' || !window.localStorage) {
    return defaultState;
  }

  try {
    const raw = window.localStorage.getItem(RUN_STORAGE_KEY);
    if (!raw) return defaultState;
    const parsed = JSON.parse(raw);
    if (parsed && typeof parsed.runs === 'object') {
      return {
        runs: parsed.runs || {},
        runOrder: Array.isArray(parsed.runOrder) ? parsed.runOrder : Object.keys(parsed.runs || {}),
        activeRunId: parsed.activeRunId || null,
        selectedEngineId: 'openfoam', // Default to OpenFOAM for new run specifications
        nextRunSequence: typeof parsed.nextRunSequence === 'number' ? parsed.nextRunSequence : 1
      };
    }
  } catch (err) {
    console.warn('[RunSlice] Error loading persisted run history:', err);
  }

  return defaultState;
}

function persistRunState(state: RunState): void {
  if (typeof window === 'undefined' || !window.localStorage) return;
  try {
    const toPersist = {
      runs: state.runs,
      runOrder: state.runOrder,
      activeRunId: state.activeRunId,
      nextRunSequence: state.nextRunSequence
    };
    window.localStorage.setItem(RUN_STORAGE_KEY, JSON.stringify(toPersist));
  } catch (err) {
    console.warn('[RunSlice] Error persisting run history:', err);
  }
}

const initialState: RunState = loadPersistedRunState();

export const runSlice = createSlice({
  name: 'run',
  initialState,
  reducers: {
    setSelectedEngine: (state, action: PayloadAction<EngineId | null>) => {
      state.selectedEngineId = action.payload;
    },

    setActiveRunId: (state, action: PayloadAction<string | null>) => {
      state.activeRunId = action.payload;
      persistRunState(state);
    },

    registerRun: (
      state,
      action: PayloadAction<{
        specification: Readonly<RunSpecification>;
        initialStatus?: RunLifecycleStatus;
      }>
    ) => {
      const spec = action.payload.specification;
      const initialStatus = action.payload.initialStatus || 'QUEUED';

      const record: RunRecord = {
        runId: spec.runId,
        studyId: spec.studyId,
        scenarioId: spec.scenarioId,
        scenarioName: spec.scenarioName,
        engine: spec.engine,
        specificationVersion: spec.specificationVersion,
        specification: spec,
        status: initialStatus,
        createdAt: spec.createdAt,
        resultStatus: 'PENDING'
      };

      state.runs[spec.runId] = record as any;
      if (!state.runOrder.includes(spec.runId)) {
        state.runOrder.unshift(spec.runId); // Most recent first
      }
      state.activeRunId = spec.runId;
      state.nextRunSequence += 1;
      state.selectedEngineId = 'openfoam'; // Default draft engine to OpenFOAM for next run

      persistRunState(state);
    },

    updateRunStatus: (
      state,
      action: PayloadAction<{
        runId: string;
        status: RunLifecycleStatus;
        remoteRunId?: string;
        error?: string;
        resultSummary?: CfdResultSummary;
      }>
    ) => {
      const { runId, status, remoteRunId, error, resultSummary } = action.payload;
      const run = state.runs[runId];
      if (!run) return;

      run.status = status;
      if (remoteRunId !== undefined) run.remoteRunId = remoteRunId;
      if (error !== undefined) run.error = error;
      if (resultSummary) {
        run.resultSummary = resultSummary;
        run.resultStatus = 'AVAILABLE';
      }
      if (status === 'COMPLETED' || status === 'FAILED' || status === 'CANCELLED') {
        run.completedAt = new Date().toISOString();
        if (status === 'FAILED' || status === 'CANCELLED') {
          if (!run.resultSummary) {
            run.resultStatus = 'NONE';
          }
        }
      }

      persistRunState(state);
    },

    clearRunHistory: (state) => {
      state.runs = {};
      state.runOrder = [];
      state.activeRunId = null;
      state.selectedEngineId = 'openfoam';
      state.nextRunSequence = 1;
      persistRunState(state);
    }
  }
});

export const {
  setSelectedEngine,
  setActiveRunId,
  registerRun,
  updateRunStatus,
  clearRunHistory
} = runSlice.actions;

export default runSlice.reducer;
